// HealthKit steps auto-sync + Apple Health badge.
//
// Behaviour
// ---------
// * On mount, if the platform is iOS native AND the user previously
//   opted in (`localStorage.calofay:healthkit_connected === "1"`),
//   we silently read today's step count and POST it to the existing
//   `/api/steps` endpoint — same shape as manual entry, so no backend
//   changes needed. Users can still manually edit; a subsequent sync
//   will simply overwrite the day's total. That's the correct
//   behaviour when the user's HealthKit total (from watch/phone
//   pedometer) is the source of truth.
// * The visible "Apple Health" chip satisfies Apple Review Guideline
//   2.5.1: "clearly identify HealthKit functionality in the app's UI".
//   The chip carries a `data-testid` so reviewers scrolling the
//   Dashboard can see it in one glance.
//
// Failure modes
// -------------
// * If HealthKit is unavailable (Android/web/simulator) we render
//   nothing.
// * If the user opted in but later revoked permission in iOS
//   Settings, we still render the chip (it means "we're trying to
//   sync") but the read will return 0 and the manual entry stays
//   authoritative.
import { useEffect, useState } from "react";
import { Heart } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n.jsx";
import {
  isHealthKitEnabled,
  isHealthKitAvailable,
  queryTodaySteps,
  queryLastNDays,
} from "@/lib/healthkit";

const OPT_IN_KEY = "calofay:healthkit_connected";
// Once we've backfilled history for an install, cache the last cursor
// so subsequent app opens only sync the small delta instead of 90 days
// of samples every time. `null` (no key) = no backfill yet.
// The `_v2` suffix forces a one-time re-backfill after the multi-source
// dedup fix (Feb 2026) — clients that already backfilled under the
// buggy code path had ~2× inflated numbers stuck in `daily_logs`.
const BACKFILL_KEY = "calofay:healthkit_last_backfill_v2";
const LEGACY_BACKFILL_KEY = "calofay:healthkit_last_backfill";
const BACKFILL_WINDOW_DAYS = 90;

function isOptedIn() {
  try { return localStorage.getItem(OPT_IN_KEY) === "1"; } catch { return false; }
}

function todayLocalIso() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toLocaleDateString("en-CA");
}

function daysSince(iso) {
  const then = new Date(iso);
  const now = new Date();
  const diff = Math.round((now - then) / 86_400_000);
  return Math.max(0, diff);
}

/**
 * Small pill that both (a) triggers a silent HealthKit → backend sync
 * on mount and (b) serves as the "Apple Health" transparency badge
 * required by Apple Review 2.5.1.
 *
 * Props:
 *   date          — YYYY-MM-DD to sync (usually today or selectedDate)
 *   onSynced      — called after a successful sync so parent can
 *                   `load()` its summary
 */
export default function HealthKitStepsSync({ date, onSynced }) {
  const { lang } = useI18n();
  const [state, setState] = useState(() => (isOptedIn() ? "syncing" : "idle"));

  useEffect(() => {
    let alive = true;
    async function sync() {
      if (!isOptedIn()) return;
      if (!(await isHealthKitAvailable())) {
        if (alive) setState("unavailable");
        return;
      }

      // ── Today first (fast, keeps the chip snappy for the current day) ──
      const steps = await queryTodaySteps();
      if (!alive) return;
      if (steps > 0) {
        try { await api.post("/steps", { date, steps, source: "healthkit" }); }
        catch (_) { /* falls through to backfill error handler */ }
      }

      // ── Then backfill the last 90 days ONE TIME per install so the
      //    progress charts have real history instead of a lonely today.
      //    Subsequent app opens only sync from the cursor forward, which
      //    is usually 1-3 days — cheap. ──
      let backfillFrom = null;
      try {
        backfillFrom = localStorage.getItem(BACKFILL_KEY);
        // Clear the legacy v1 key so it doesn't confuse anyone reading
        // localStorage in the debugger.
        if (localStorage.getItem(LEGACY_BACKFILL_KEY)) {
          localStorage.removeItem(LEGACY_BACKFILL_KEY);
        }
      } catch {}
      const daysToFetch = backfillFrom
        ? Math.max(1, Math.min(BACKFILL_WINDOW_DAYS, daysSince(backfillFrom) + 1))
        : BACKFILL_WINDOW_DAYS;
      try {
        const rows = await queryLastNDays(daysToFetch);
        // Drop zero-step days so we don't overwrite manual entries
        // with 0 (HealthKit returns 0 for both "no steps" AND
        // "permission denied", which is why we're defensive).
        const entries = rows
          .filter(r => r.healthKitSteps > 0 && r.date !== date)
          .map(r => ({ date: r.date, steps: r.healthKitSteps }));
        if (entries.length) {
          await api.post("/steps/batch", { entries, source: "healthkit" });
        }
        try { localStorage.setItem(BACKFILL_KEY, todayLocalIso()); } catch {}
      } catch (e) {
        if (!alive) return;
        setState("error");
        return;
      }

      if (!alive) return;
      setState(steps > 0 ? "synced" : "no_data");
      onSynced?.();
    }
    sync();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  // Render nothing on Web/Android/simulator or when the user hasn't
  // opted in — we only surface the badge for users who've explicitly
  // connected HealthKit, so we're honest about the data source.
  if (!isHealthKitEnabled() || !isOptedIn()) return null;

  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] font-semibold text-rose-600 bg-rose-50 border border-rose-100 rounded-full px-2 py-0.5"
      data-testid="healthkit-badge"
      title={lang === "en" ? "Steps synced from Apple Health" : "الخطوات مزامنة من Apple Health"}
    >
      <Heart className="w-3 h-3" />
      {lang === "en" ? "Apple Health" : "Apple Health"}
      {state === "syncing" && <span className="opacity-60"> · …</span>}
    </span>
  );
}

/**
 * One-shot connect/disconnect helper used by the Settings toggle.
 * Returns the new state (true=connected, false=not connected).
 * Never throws — silent failure is fine because the badge will
 * simply not appear next time.
 */
export async function toggleHealthKitConnection(enable) {
  if (!enable) {
    try { localStorage.removeItem(OPT_IN_KEY); } catch {}
    return false;
  }
  if (!(await isHealthKitAvailable())) return false;
  try {
    // Dynamic import so users who never open the toggle don't pull in
    // the request-authorization module.
    const m = await import("@/lib/healthkit");
    await m.requestStepReadPermission();
    // Probe a query to check whether the user actually granted access.
    // Apple's SDK hides the decision so a successful probe = permission.
    const steps = await m.queryTodaySteps();
    // Even if today's step count is 0, we mark opted-in — the user
    // may just be starting the day. A later sync will pick up real
    // numbers as they walk.
    try { localStorage.setItem(OPT_IN_KEY, "1"); } catch {}
    return true;
  } catch (e) {
    console.warn("[HealthKit] toggle failed", e);
    return false;
  }
}

export function isHealthKitConnected() {
  return isOptedIn();
}
